<template>
    <div class="content">
        <div class="container-fluid">
            <!-- Titre & breadcrumb -->
            <div class="page-heading row justify-content-between align-items-center py-3">
                <div class="col-lg-4">
                    <h4 class="page-title">{{ $t('account.title') }}</h4>
                </div>
                <div class="col-auto order-2 d-none d-sm-block">
                    <ol class="breadcrumb m-0 py-2">
                        <li class="breadcrumb-item">
                            <router-link to="/installation-list">{{ $t('common.installerSpace') }}</router-link>
                        </li>
                        <li class="breadcrumb-item active">{{ $t('nav.account') }}</li>
                    </ol>
                </div>
            </div>

            <form @submit.prevent="handleSubmit" class="pb-4">
                <div class="row">
                    <div class="col-lg-8 col-xl-9 d-flex flex-column">
                        <!-- Informations d'identification -->
                        <div class="card flex-shrink-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('account.identInfo') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="email" class="form-label">{{ $t('common.email') }}</label>
                                        <input type="email" class="form-control" v-model="installer.email" required :placeholder="$t('account.emailPlaceholder')" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="mobile" class="form-label">{{ $t('common.mobilePhone') }}</label>
                                        <vue-tel-input class="form-control" v-model="mobile_phone_number" @validate="onValidPhoneNumber" :validCharactersOnly="true" :inputOptions="{required:true, placeholder:$t('account.phonePlaceholder'), id:'phone-input'}"></vue-tel-input>
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <PasswordComponent id="password" :label="$t('changepwd.newPassword')" :required="false" v-model="installer.password"/>
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <PasswordComponent id="confirmed-password" :label="$t('changepwd.confirmPassword')" :required="false" v-model="installer.confirmPassword" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="mfa" class="form-label">{{ $t('account.mfaMethod') }}</label>
                                        <select class="form-select" v-model="installer.mfa">
                                            <option value="none">{{ $t('account.mfa.passwordOnly') }}</option>
                                            <option value="email">{{ $t('account.mfa.passwordEmail') }}</option>
                                            <option value="sms">{{ $t('account.mfa.passwordSMS') }}</option>
                                            <option value="totp">{{ $t('account.mfa.passwordApp') }}</option>
                                            <!-- Needs a phone enrolled from the Haxo Setup app (it enrolls itself at sign-in). -->
                                            <option value="push" :disabled="authenticators.length === 0 && installer.mfa !== 'push'">{{ $t('account.mfa.passwordPush') }}<template v-if="authenticators.length === 0"> - {{ $t('account.mfa.passwordPushNoPhone') }}</template></option>
                                        </select>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Trusted phones: the ones approving the sign-ins (enrolled from the mobile app) -->
                        <div class="card flex-shrink-0">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('account.devices.title') }}</h4>
                            </div>
                            <div class="card-body">
                                <p v-if="authenticators.length === 0" class="text-muted mb-0">{{ $t('account.devices.empty') }}</p>
                                <div v-else class="table-responsive">
                                    <table class="table table-sm table-centered mb-0">
                                        <thead>
                                            <tr>
                                                <th>{{ $t('account.devices.name') }}</th>
                                                <th>{{ $t('account.devices.added') }}</th>
                                                <th>{{ $t('account.devices.lastUsed') }}</th>
                                                <th>{{ $t('account.devices.push') }}</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="device in authenticators" :key="device.id">
                                                <td>{{ device.name }} <small class="text-muted">({{ device.platform }})</small></td>
                                                <td>{{ formatDate(device.created_at) }}</td>
                                                <td>{{ device.last_used_at ? formatDate(device.last_used_at) : $t('account.devices.never') }}</td>
                                                <td>{{ device.has_push ? $t('common.yes') : $t('common.no') }}</td>
                                                <td class="text-end">
                                                    <button type="button" class="btn btn-sm btn-outline-danger" @click="confirmRemoveDevice(device)">{{ $t('account.devices.remove') }}</button>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- Informations personnelles -->
                        <div class="card card-h-100">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('account.personalInfo') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="row">
                                    <div class="mb-2 col-md-6">
                                        <label for="nom" class="form-label">{{ $t('common.lastName') }}</label>
                                        <input type="text" class="form-control" id="nom" v-model="installer.name" required maxlength="32" :placeholder="$t('member.lastNamePlaceholder')" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="prenom" class="form-label">{{ $t('common.firstName') }}</label>
                                        <input type="text" class="form-control" id="prenom" v-model="installer.first_name" required maxlength="32" :placeholder="$t('member.firstNamePlaceholder')" />
                                    </div>
                                    <div class="mb-2 col-md-6">
                                        <label for="societe" class="form-label">{{ $t('common.company') }}</label>
                                        <input type="text" class="form-control" id="societe" v-model="installer.company_name" required maxlength="32" :placeholder="$t('member.companyPlaceholder')" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="card flex-shrink-0">
                            <div class="card-header">
                                <div class="d-flex align-items-center justify-content-between">
                                    <h4 class="header-title mb-0">{{ $t('account.receiveAlerts') }}</h4>
                                    <div class="form-check mb-0">
                                        <input type="checkbox" id="alertes" v-model="installer.alert" :data-switch="true" />
                                        <label for="alertes" :data-on-label="$t('common.yes')" :data-off-label="$t('common.no')"></label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="col-lg-4 col-xl-3 d-flex flex-column">
                        <!-- Photo ou logo -->
                        <div class="card flex-grow-1">
                            <div class="card-header pb-0">
                                <h4 class="header-title">{{ $t('common.photoOrLogo') }}</h4>
                            </div>
                            <div class="card-body">
                                <div class="d-flex h-100 flex-wrap justify-content-center align-items-center" ref="imageContainer">
                                    <!-- Affichage photo -->
                                    <div v-if="installer.picture" class="media-viewer border rounded">
                                        <img :src="installer.picture" alt="image" class="media-viewer-img object-fit-cover" :style="{ maxHeight: imageMaxHeight }">
                                        <div class="media-viewer-tools">
                                            <button type="button" class="btn btn-white text-danger" @click="removePicture">
                                                <i class="mdi mdi-delete me-1"></i>{{ $t('common.delete') }}
                                            </button>
                                        </div>
                                    </div>
                                    <!-- Ajouter photo -->
                                    <div v-else class="media-add-file single-file">
                                        <button type="button" class="btn btn-secondary"
                                            @click="handleFileUpload">{{ $t('common.addFile') }}</button>
                                        <input type="file" id="addFileInput" class="d-none" ref="fileInput"
                                            @change="handleFileChange" />
                                        <span class="help-block mt-1">
                                            <small>{{ $t('common.imageRequirements') }}</small>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Actions du formulaire -->
                <div class="form-actions-sticky d-flex justify-content-center align-items-center">
                    <router-link to="/installation-list" class="btn btn-outline-secondary me-2">{{ $t('common.back') }}</router-link>
                    <button type="submit" class="btn btn-primary">{{ $t('common.save') }}</button>
                </div>
            </form>
        </div>
    </div>
    <MfaModal ref="mfaModal" :action="updateInstaller" />
    <ApproveModal ref="approveModal" :action="updateInstaller" />
    <ApproveModal ref="deviceApproveModal" :action="removeDeviceApproved" purpose="phone_removal" />
    <TotpModal ref="totpModal" :action="updateInstallerTotp" />
    <ConfirmationModal ref="deviceModal" :title="$t('account.devices.title')" :question="deviceQuestion" type="danger" :button1="$t('common.cancel')" :button2="$t('account.devices.remove')" :action="removeDevice" />
</template>

<script>
import { useUserStore } from '@/stores/userStore';
import { onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { showToast } from '@/services/toast'
import PasswordComponent from '@/components/password';
import MfaModal from '@/modals/mfaModal';
import ApproveModal from '@/modals/approveModal.vue';
import TotpModal from '@/modals/totpModal.vue';
import ConfirmationModal from '@/modals/confirmationModal.vue';

export default {
    name: 'AccountPage',
    components: {
        PasswordComponent,
        MfaModal,
        ApproveModal,
        TotpModal,
        ConfirmationModal,
    },
    setup() {
        const userStore = useUserStore();
        const { t, locale } = useI18n();
        const installer = reactive(userStore.installer.clone());
        const mfaModal = ref(null);
        const mfaMessage = ref('');
        const approveModal = ref(null);
        const totpModal = ref(null);
        // Phones enrolled to approve the sign-ins (push second factor).
        const authenticators = ref([]);
        const deviceModal = ref(null);
        const deviceQuestion = ref('');
        const deviceApproveModal = ref(null);
        // Phone whose removal waits for the approval on the account's phone.
        const pendingRemoval = ref(null);
        const imageContainer = ref(null);
        const imageMaxHeight = ref("100px");
        const mobile_phone_number = ref(installer.mobile_phone_number);
        const validPhoneNumber = ref(false);

        const handleSubmit = async () => {
            if(validate()) {
                installer.totp = null;
                if (installer.mfa == 'totp' && userStore.installer.mfa != 'totp') {
                    totpModal.value.show(installer.email);
                } else {
                    await updateInstaller();
                }
            }
        };

        const onValidPhoneNumber = ({ valid }) => {
            validPhoneNumber.value = valid;
            if(valid) {
                const input = document.getElementById('phone-input');
                if(input) {
                    input.setCustomValidity('');
                }
            }
        };

        const validate = () => {
            if(installer.password.length > 0 || installer.confirmPassword.length > 0) {
                const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
                if (!passwordRegex.test(installer.password)) {
                    displayError('password', t('common.passwordRequirements'));
                    return false;
                }
                if(installer.password != installer.confirmPassword) {
                    displayError('confirmed-password', t('common.passwordsMustMatch'));
                    return false;
                }
            }
            if(!validPhoneNumber.value) {
                displayError('phone-input', t('common.phoneInvalid'));
                return false;
            }
            return true;
        };

        const displayError = (id, error) => {
            const input = document.getElementById(id);
            input.setCustomValidity(error);
            input.reportValidity();
        }

        const updateInstallerTotp = async (totp) => {
            installer.totp = totp;
            await updateInstaller();
        };

        const updateInstaller = async (code) => {
            try {
                installer.code = code;
                installer.mobile_phone_number = mobile_phone_number.value.replaceAll(' ', '');
                const result = await userStore.updateInstaller(installer);
                if(result === 'need_mfa') {
                    // The approval on a phone has no code to type: the modal
                    // waits for the phone, then sends the request id back as
                    // the code.
                    if (userStore.mfa_info.method === 'push' && userStore.mfa_info.challenge_id) {
                        approveModal.value.show(userStore.mfa_info);
                    } else {
                        mfaModal.value.show(userStore.mfa_info.message);
                    }
                } else {
                    showToast('success', t('common.updateSuccess'));
                }
            } catch (error) {
                showToast('error', error);
            }
        }

        const handleFileChange = (event) => {
            const file = event.target.files[0];
            if (file) {
                if (file.type.startsWith('image/')) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const base64Image = reader.result;
                        installer.picture = base64Image;
                    };
                    reader.readAsDataURL(file);
                } else {
                    showToast('error', t('common.invalidImage'));
                }
            }
        };

        const handleFileUpload = () => {
            document.getElementById('addFileInput').click();
        };

        const removePicture = () => {
            installer.picture = '';
        };

        const setImageMaxHeight = (() => {
            if(imageContainer.value) {
                const rect = imageContainer.value.getBoundingClientRect();
                imageMaxHeight.value = `${rect.height * 0.9}px`;
            }
        });

        const formatDate = (value) => new Date(value).toLocaleString(locale.value);

        const confirmRemoveDevice = (device) => {
            deviceQuestion.value = t('account.devices.removeQuestion', { name: device.name });
            deviceModal.value.show(device);
        };

        const removeDevice = async (device, code) => {
            const result = await userStore.removeAuthenticator(device.id, code);

            // The phones are the account's second factor: the server opened a
            // request instead of removing anything. The modal waits for the
            // phone, then calls back with the approved request's id, which
            // completes the removal.
            if (result?.result === 'need_approval') {
                pendingRemoval.value = device;
                deviceApproveModal.value.show(result.mfa);
                return;
            }

            switch (result) {
                case 'ok':
                    authenticators.value = authenticators.value.filter(d => d.id !== device.id);
                    showToast('success', t('account.devices.removed'));
                    break;
                case 'last':
                    showToast('error', t('account.devices.removeLast'));
                    break;
                default:
                    showToast('error', t('account.devices.removeFailed'));
                    break;
            }
        };

        const removeDeviceApproved = async (code) => {
            const device = pendingRemoval.value;
            pendingRemoval.value = null;
            if (device) await removeDevice(device, code);
        };

        onMounted(async () => {
            setImageMaxHeight();
            window.addEventListener("resize", setImageMaxHeight);
            authenticators.value = await userStore.loadAuthenticators();
        });

        return {
            installer,
            authenticators,
            deviceModal,
            deviceQuestion,
            formatDate,
            confirmRemoveDevice,
            removeDevice,
            deviceApproveModal,
            removeDeviceApproved,
            handleSubmit,
            handleFileChange,
            handleFileUpload,
            removePicture,
            totpModal,
            mfaModal,
            mfaMessage,
            approveModal,
            updateInstaller,
            updateInstallerTotp,
            imageContainer,
            imageMaxHeight,
            mobile_phone_number,
            onValidPhoneNumber,
        };
    }
};
</script>

<style scoped>
:deep(.vue-tel-input.form-control) {
    display: flex;
}

:deep(.vue-tel-input.form-control .vti__input) {
    color: var(--ct-primary-color);
}

:deep(.vue-tel-input.form-control .vti__dropdown) {
    padding: 0;
}

:deep(.vue-tel-input.form-control .vti__input::placeholder) {
    color: var(--ct-secondary-color);
    opacity: 1;
}

:deep(.vue-tel-input.form-control:focus),
:deep(.vue-tel-input.form-control:focus-within),
:deep(.vue-tel-input.form-control .vti__input:focus),
:deep(.vue-tel-input.form-control .vti__input:focus-visible),
:deep(.vue-tel-input.form-control .vti__input:-webkit-autofill),
:deep(.vue-tel-input.form-control .vti__input::-webkit-focus-ring-color) {
  outline: none !important;
  box-shadow: none !important;
  border-color: transparent !important;
}

:deep(.vue-tel-input.form-control:focus-within) {
  border: 1px solid var(--ct-border-color) !important;
}
</style>
